import { TOKEN_AUDIENCE } from '@ibe/constants';
import { nowIso } from '@ibe/utils';
import {
  type ApiErrorBody,
  type ApiErrorResponse,
  type ApiListResponse,
  type ApiSuccessResponse,
  type PaginationMeta,
} from '@ibe/types';

const NO_CONTENT = 204;

/**
 * The JWT's audience claim, decoded client-side. Only a staff session carries
 * the refresh cookie `/v1/auth/refresh` exchanges — guest and
 * reservation-scoped tokens have nothing to exchange, so sending them there
 * just earns a TOKEN_INVALID and burns a round trip.
 */
function tokenAudienceOf(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (payload === undefined) return null;
    const claims: unknown = JSON.parse(atob(payload));
    if (typeof claims !== 'object' || claims === null) return null;
    const aud = (claims as { aud?: unknown }).aud;
    return typeof aud === 'string' ? aud : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string;
  readonly fieldErrors: Readonly<Record<string, string>>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.correlationId = body.correlationId;
    this.fieldErrors = Object.fromEntries(
      (body.fieldErrors ?? []).map((error) => [error.field, error.message]),
    );
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the server. Check your connection and try again.', { cause });
    this.name = 'NetworkError';
  }
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly signal?: AbortSignal;
  readonly idempotencyKey?: string;
  readonly raw?: boolean;
}

export interface ListResult<TItem> {
  readonly items: readonly TItem[];
  readonly pagination: PaginationMeta;
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), `${baseUrl.replace(/\/$/, '')}/`);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    url.searchParams.set(key, String(value));
  }

  return url.toString();
}

function errorBodyOf(value: unknown): ApiErrorBody | null {
  if (typeof value !== 'object' || value === null) return null;

  const candidate: unknown = (value as Partial<ApiErrorResponse>).error;
  if (typeof candidate !== 'object' || candidate === null) return null;

  const body = candidate as Partial<ApiErrorBody>;
  if (typeof body.code !== 'string' || typeof body.message !== 'string') return null;

  return candidate as ApiErrorBody;
}

const UNAUTHORIZED = 401;

type RefreshOutcome = 'refreshed' | 'refused' | 'unreachable';

export class ApiClient {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #expiryListeners = new Set<() => void>();
  #accessToken: string | null = null;
  #refreshInFlight: Promise<RefreshOutcome> | null = null;

  constructor(options: ApiClientOptions) {
    this.#baseUrl = options.baseUrl;
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  get accessToken(): string | null {
    return this.#accessToken;
  }

  setAccessToken(token: string | null): void {
    this.#accessToken = token;
  }

  onSessionExpired(listener: () => void): () => void {
    this.#expiryListeners.add(listener);
    return () => {
      this.#expiryListeners.delete(listener);
    };
  }

  #notifyExpired(): void {
    for (const listener of this.#expiryListeners) {
      try {
        listener();
      } catch {
        // Ignore errors in listener callbacks.
      }
    }
  }

  async request<TResult>(path: string, options: RequestOptions = {}): Promise<TResult> {
    const sentToken = this.#accessToken;
    const response = await this.#send(path, options);

    if (response.status !== UNAUTHORIZED) {
      return await this.#parse(response, options.raw ?? false);
    }

    // A 401 on a request that carried no bearer token is an unauthenticated rejection,
    // not an expired session.
    if (sentToken === null) {
      return await this.#parse(response, options.raw ?? false);
    }

    // Attempt token refresh via the refresh cookie — staff sessions only.
    // Guest and reservation tokens are not refreshable; treating them as
    // staff sessions surfaced a TOKEN_INVALID and cleared the stored token
    // for nothing.
    const outcome =
      tokenAudienceOf(sentToken) === TOKEN_AUDIENCE.STAFF ? await this.#refresh() : 'refused';

    if (outcome === 'refreshed') {
      // Retry with the refreshed token
      const retried = await this.#send(path, options);
      if (retried.status !== UNAUTHORIZED) {
        return await this.#parse(retried, options.raw ?? false);
      }
    }

    // Refresh refused, or the refreshed token still drew a 401 — the session
    // has ended, so clear the token and notify listeners. An unreachable
    // refresh endpoint is not proof of expiry: keep the token so a transient
    // outage doesn't sign the guest out.
    if (outcome !== 'unreachable') {
      this.setAccessToken(null);
      this.#notifyExpired();
    }

    // Now attempt a fallback retry WITHOUT the bearer token.
    // If the requested endpoint is public (e.g. /v1/properties/locations/tree, search, catalogue),
    // the unauthenticated retry will succeed and avoid crashing the page!
    try {
      const anonResponse = await this.#send(path, options, false);
      if (anonResponse.ok || anonResponse.status === NO_CONTENT) {
        return await this.#parse(anonResponse, options.raw ?? false);
      }
    } catch {
      // Fallback failed, continue to throw original error
    }

    return await this.#parse(response, options.raw ?? false);
  }

  async #refresh(): Promise<RefreshOutcome> {
    this.#refreshInFlight ??= this.#performRefresh().finally(() => {
      this.#refreshInFlight = null;
    });

    return await this.#refreshInFlight;
  }

  async #performRefresh(): Promise<RefreshOutcome> {
    try {
      const response = await this.#fetch(buildUrl(this.#baseUrl, '/v1/auth/refresh', undefined), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return 'refused';

      const body: unknown = await response.json();
      const token = (body as { data?: { accessToken?: unknown } }).data?.accessToken;
      if (typeof token !== 'string') return 'refused';

      this.setAccessToken(token);
      return 'refreshed';
    } catch {
      return 'unreachable';
    }
  }

  async get<TData>(path: string, query?: RequestOptions['query']): Promise<TData> {
    return await this.request<TData>(path, { method: 'GET', ...(query ? { query } : {}) });
  }

  async list<TItem>(path: string, query?: RequestOptions['query']): Promise<ListResult<TItem>> {
    const envelope = await this.request<ApiListResponse<TItem>>(path, {
      method: 'GET',
      raw: true,
      ...(query ? { query } : {}),
    });
    return { items: envelope.data, pagination: envelope.pagination };
  }

  async post<TData>(path: string, body?: unknown, idempotencyKey?: string): Promise<TData> {
    return await this.request<TData>(path, {
      method: 'POST',
      body,
      ...(idempotencyKey === undefined ? {} : { idempotencyKey }),
    });
  }

  async put<TData>(path: string, body?: unknown): Promise<TData> {
    return await this.request<TData>(path, { method: 'PUT', body });
  }

  async patch<TData>(path: string, body?: unknown): Promise<TData> {
    return await this.request<TData>(path, { method: 'PATCH', body });
  }

  async delete<TData>(path: string): Promise<TData> {
    return await this.request<TData>(path, { method: 'DELETE' });
  }

  async #send(path: string, options: RequestOptions, sendAuthHeader = true): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    if (sendAuthHeader && this.#accessToken !== null) {
      headers.Authorization = `Bearer ${this.#accessToken}`;
    }
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (options.idempotencyKey !== undefined) {
      headers['Idempotency-Key'] = options.idempotencyKey;
    }

    try {
      return await this.#fetch(buildUrl(this.#baseUrl, path, options.query), {
        method: options.method ?? 'GET',
        headers,
        credentials: 'include',
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new NetworkError(error);
    }
  }

  async #parse<TResult>(response: Response, raw: boolean): Promise<TResult> {
    if (response.status === NO_CONTENT) return undefined as TResult;

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      if (response.ok) return undefined as TResult;
      body = undefined;
    }

    if (!response.ok) {
      const errorBody = errorBodyOf(body);
      if (errorBody !== null) throw new ApiError(response.status, errorBody);

      throw new ApiError(response.status, {
        code: 'UNEXPECTED_RESPONSE',
        message: 'The server returned an unexpected response.',
        correlationId: '',
        requestId: '',
        timestamp: nowIso(),
      });
    }

    return raw ? (body as TResult) : (body as ApiSuccessResponse<TResult>).data;
  }
}
